import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Download, Sparkles, RefreshCw, HelpCircle } from 'lucide-react';
import ImprovedAnalyzerCard from '../components/ImprovedAnalyzerCard';
import RobotsTxtDisplay from '../components/RobotsTxtDisplay';
import MarkdownList from '../components/MarkdownList';
import UsageBadge from '../components/UsageBadge';
import UserMenu from '../components/UserMenu';
import PricingModal from '../components/PricingModal';
import FairUsePolicyModal from '../components/FairUsePolicyModal';
import StatsBar from '../components/StatsBar';
import GuideModal from '../components/GuideModal';
import { getScoreColor, getGradeColor, formatProcessingTime, formatUrl, getGroupDisplayName, getGroupDescription } from '../utils/helpers';
import VersionFooter from '../components/VersionFooter';
// At top of Dashboard.jsx (around line 11)
import BotBlockingAlert from '../components/BotBlockingAlert';
// At top with other imports
import SubscriptionPlans from '../components/SubscriptionPlans';
import BillingManagement from '../components/BillingManagement';
import useCanonical from '../hooks/useCanonical';

const Dashboard = ({ user: userProp, onLogout: onLogoutProp }) => {
  useCanonical('https://aeo.thatworkx.com/dashboard');
  const location = useLocation();
  const navigate = useNavigate();
  const { result: initialResult } = location.state || {};
  const [result, setResult] = useState(initialResult);
  const [analysisId, setAnalysisId] = useState(initialResult?.analysisId || null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingModalTab, setPricingModalTab] = useState('subscription');
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [guideModalTab, setGuideModalTab] = useState('howto');
  const [showFairUseModal, setShowFairUseModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [user, setUser] = useState(userProp); // Initialize from prop
  const [usage, setUsage] = useState(result?.usage || null); // Track usage separately
  // Around line 22, with other state declarations
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState(false);
  const [showBillingManagement, setShowBillingManagement] = useState(false);

  // Fetch user data and usage on component mount
  React.useEffect(() => {
    const fetchUserAndUsage = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_URL}/api/auth/session`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          // Update usage from session data
          if (data.usage) {
            setUsage(data.usage);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    fetchUserAndUsage();
  }, []);

  // Handle subscription success/cancel redirect from Stripe
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subscriptionStatus = params.get('subscription');
    
    if (subscriptionStatus === 'success') {
      // Show success message
      alert('🎉 Welcome to Pro! Your subscription is now active. Refreshing your account...');
      
      // Force refresh user data to get updated subscription
      const refreshUser = async () => {
        try {
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          const response = await fetch(`${API_URL}/api/auth/session`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
            console.log('User subscription updated:', data.user?.subscription);
          }
        } catch (error) {
          console.error('Failed to refresh user:', error);
        }
      };
      
      refreshUser();
      
      // Redirect to home page after brief delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
      
    } else if (subscriptionStatus === 'cancelled') {
      // User cancelled checkout
      alert('Checkout cancelled. You can upgrade anytime!');
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);
 

  // Fallback redirect if no result is found (e.g. on direct page reload)
  // But allow subscription success/cancel parameters to be processed first
  useEffect(() => {
    if (!result && !location.search.includes('subscription=')) {
      const timer = setTimeout(() => {
        navigate('/', { replace: true });
      }, 500); // Small delay to allow potential state hydration
      return () => clearTimeout(timer);
    }
  }, [result, location.search, navigate]);

  // Logout handler
  const handleLogout = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      onLogoutProp(); // Clear user in App.js
      navigate('/'); // Navigate to landing page
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/'); // Still navigate even if logout call fails
    }
  };

  if (!result) {
    return (
      <div className="min-h-screen bg-dark-950 flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Redirecting...</h2>
          <p className="text-dark-400 mb-6">No analysis data found. Returning to the homepage.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-dark-800 hover:bg-dark-700 text-white font-medium rounded-lg transition-colors border border-dark-700"
          >
            Go Back Now
          </button>
        </div>
      </div>
    );
  }

  const { results = {}, error: topLevelError } = result || {};
  
  // Safe extraction: prefer fields from results, but fallback to top-level for errors
  const analysisUrl = results?.url || result?.url || '';
  const overallScore = results?.overallScore || 0;
  const overallGrade = results?.overallGrade || 'F';
  const analyzers = results?.analyzers || {};
  const recommendations = results?.recommendations || [];
  const processingTime = results?.processingTime || 0;
  const weights = results?.weights || {
    technicalFoundation: 0.20,
    contentStructure: 0.20,
    pageLevelEEAT: 0.15,
    siteLevelEEAT: 0.15,
    aiVisibility: 0.10,
    robotsTxt: 0.10,
    markdownFiles: 0.10,
    queryMatch: 0.00
  };
  
  // Check if there's an error (bot blocking, etc.)
  const analysisError = topLevelError || result.error || null;
  
  // Use usage from state (updated from session) or fallback to result.usage
  const currentUsage = usage || result.usage;

  const analyzerGroups = [
    {
      id: 'discovery',
      title: getGroupDisplayName('discovery'),
      description: getGroupDescription('discovery'),
      color: 'bg-indigo-900/20 border-indigo-700',
      keys: ['robotsTxt', 'markdownFiles', 'technicalFoundation']
    },
    {
      id: 'clarity',
      title: getGroupDisplayName('clarity'),
      description: getGroupDescription('clarity'),
      color: 'bg-dark-800/50 border-dark-700',
      keys: ['contentStructure', 'aiVisibility']
    },
    {
      id: 'trust',
      title: getGroupDisplayName('trust'),
      description: getGroupDescription('trust'),
      color: 'bg-dark-800/50 border-dark-700',
      keys: ['pageLevelEEAT', 'siteLevelEEAT']
    },
    {
      id: 'relevance',
      title: getGroupDisplayName('relevance'),
      description: getGroupDescription('relevance'),
      color: 'bg-dark-800/50 border-dark-700',
      keys: ['queryMatch']
    }
  ];
  
  // DEBUG: Log error data to see what we're receiving
  if (analysisError) {
    console.log('🐛 Analysis Error Data:', JSON.stringify(analysisError, null, 2));
    console.log('🐛 Block Detection:', analysisError.blockDetection);
  }

  // Determine which band the score falls into
  const getScoreBand = (score) => {
    if (score >= 90) return { grade: 'A', color: 'bg-green-500', label: 'A (90-100)' };
    if (score >= 80) return { grade: 'B', color: 'bg-blue-500', label: 'B (80-89)' };
    if (score >= 70) return { grade: 'C', color: 'bg-yellow-500', label: 'C (70-79)' };
    if (score >= 60) return { grade: 'D', color: 'bg-orange-500', label: 'D (60-69)' };
    return { grade: 'F', color: 'bg-red-500', label: 'F (0-59)' };
  };

  const handleExportPDF = async (type) => {
    // Intercept for free users - show upgrade modal
    if (!user || (user.subscription?.type !== 'pro' && user.subscription?.type !== 'enterprise')) {
      setShowSubscriptionPlans(true);
      return;
    }

    try {
      setIsExporting(true);
      
      // Check if we have an analysisId
      if (!analysisId) {
        throw new Error('Analysis ID not found. Please run a new analysis.');
      }
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_URL}/api/export/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          analysisId: analysisId,  // Use the stored analysisId
          type
        })
      });
        
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Export failed');
      }
        
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aeo-report-${type}-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
        
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF. ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_URL}/api/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          url: results.url,
          targetKeywords: results.targetKeywords || []
        }),
      });

      const data = await response.json();
      
      // Handle both success and bot blocking cases
      if (data.success) {
        // Update the result state with new analysis
        setResult({
          results: data.results,
          usage: data.usage,
          analysisId: data.analysisId  // Store the analysisId
        });
        setAnalysisId(data.analysisId);  // Update analysisId state
      } else if (data.error) {
        // Bot blocking or other analysis error
        setResult({
          results: results,  // Keep existing results for display
          usage: data.usage || usage,
          error: data  // Store full error object with blockDetection
        });
      } else {
        alert('Refresh failed: Unknown error');
      }
    } catch (error) {
      console.error('Refresh error:', error);
      alert('Failed to refresh analysis. Network error - please check your connection.');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      {/* Stats Bar */}
      <StatsBar />
    <div className="min-h-screen bg-gradient-to-b from-dark-950 via-dark-900 to-dark-950">
      {/* Header */}
      <header className="border-b border-dark-800 sticky top-0 bg-dark-950/80 backdrop-blur-sm z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-primary-500" />
            <img 
              src="/AEO-thatworkx-logo.svg" 
              alt="AEO Suite by Thatworkx" 
              className="h-16"
            />
          </div>

          <div className="flex items-center gap-4">
            {currentUsage && (
              <UsageBadge current={currentUsage.current} limit={currentUsage.limit} />
            )}
            <button
              onClick={() => setShowSubscriptionPlans(true)}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors text-sm"
            >
              Upgrade to Pro
            </button>
            {/* Help/Documentation Button */}
            <button
              onClick={() => {
                setGuideModalTab('business');
                setShowGuideModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-dark-100 font-medium rounded-lg transition-colors border border-dark-700"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Documentation</span>
            </button>
            {/* User Menu with Email and Logout */}
            <UserMenu 
              user={user} 
              onLogout={handleLogout}
              onManageSubscription={() => setShowBillingManagement(true)}
              onUpgrade={() => setShowSubscriptionPlans(true)}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Action Buttons */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white font-medium rounded-lg transition-colors border border-dark-700"
          >
            <ArrowLeft size={20} />
            <span>New Analysis</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white font-medium rounded-lg transition-colors text-sm border border-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Analysis'}
          </button>
          
          {/* PDF Export Buttons - Enhanced Teaser Logic */}
          <button
            onClick={() => handleExportPDF('summary')}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-gray-600 text-white rounded-lg transition-colors text-sm"
          >
            <Download size={16} />
            Summary PDF
            {(!user || (user.subscription?.type !== 'pro' && user.subscription?.type !== 'enterprise')) && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 text-[10px] font-bold rounded uppercase">Pro</span>
            )}
          </button>
          <button
            onClick={() => handleExportPDF('detailed')}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-gray-600 text-white rounded-lg transition-colors text-sm"
          >
            <Download size={16} />
            Detailed PDF
            {(!user || (user.subscription?.type !== 'pro' && user.subscription?.type !== 'enterprise')) && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 text-[10px] font-bold rounded uppercase">Pro</span>
            )}
          </button>
        </div>

        {/* Analysis Summary Bar */}
        <div className="mb-6 bg-dark-800/50 border border-dark-700 rounded-lg px-5 py-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            {/* URL */}
            <div className="flex items-center gap-2">
              <ExternalLink size={14} className="text-primary-500" />
              <span className="text-gray-400">Analyzing:...</span>
              <a 
                href={analysisUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white font-medium hover:text-primary-500 transition-colors max-w-md truncate"
              >
                {formatUrl(analysisUrl)}
              </a>
            </div>

            {/* Separator */}
            <div className="hidden sm:block w-px h-4 bg-dark-600"></div>

            {/* Date/Time */}
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gray-400">Analyzed:</span>
              <span className="text-white font-medium">
                {new Date(results.timestamp || Date.now()).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>

            {/* Separator */}
            <div className="hidden sm:block w-px h-4 bg-dark-600"></div>

            {/* Processing Time */}
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-gray-400">Completed in:</span>
              <span className="text-white font-medium">{formatProcessingTime(processingTime)}</span>
            </div>

            {/* Separator */}
            <div className="hidden sm:block w-px h-4 bg-dark-600"></div>

            {/* Status Summary */}
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gray-400">Status:</span>
              <span className={`font-bold ${
                overallScore >= 90 ? 'text-green-400' :
                overallScore >= 70 ? 'text-blue-400' :
                overallScore >= 50 ? 'text-yellow-400' :
                overallScore >= 30 ? 'text-orange-400' :
                'text-red-400'
              }`}>
                {analysisError ? 'CRITICAL - Bot Blocking' :
                 overallScore >= 90 ? 'EXCELLENT' :
                 overallScore >= 70 ? 'GOOD' :
                 overallScore >= 50 ? 'NEEDS WORK' :
                 overallScore >= 30 ? 'POOR' :
                 'CRITICAL'}
              </span>
            </div>
          </div>
        </div>

        {/* Bot Blocking Alert - BotBlockingAlert Component */}
        {analysisError && analysisError.blockDetection && analysisError.blockDetection.isBlocked && (
          <BotBlockingAlert 
            url={analysisUrl}
            botBlocking={analysisError.blockDetection}
          />
        )}

        {/* Overall Score Card - FIXED GRADE DISPLAY */}
        <div className="mb-8">
          <div className="bg-gradient-to-br from-dark-800 to-dark-900 border border-dark-700 rounded-2xl p-8 shadow-2xl">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <div className="text-sm text-dark-400 mb-2 flex items-center gap-2">
                  <ExternalLink size={14} />
                  <span>{formatUrl(analysisUrl)}</span>
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">
                  Analysis Complete
                </h2>
                <p className="text-dark-400">
                  Processed in {formatProcessingTime(processingTime)}
                </p>
              </div>

              <div className="text-right">
                <div className="text-sm text-dark-400 mb-2">Overall Score</div>
                <div className={`text-6xl font-bold ${getScoreColor(overallScore)} mb-2`}>
                  {overallScore}
                </div>
                <div className={`inline-block px-4 py-2 rounded-full text-lg font-bold ${getGradeColor(overallGrade)}`}>
                  Grade {overallGrade}
                </div>
              </div>
            </div>

            {/* Trend Indicator - Pro Feature */}
            {result.trend && (user?.subscription?.type === 'pro' || user?.subscription?.type === 'enterprise') && user?.hasActiveSubscription && (
              <div className={`${
                result.trend.trend === 'up' ? 'bg-green-900/20 border-green-700' :
                result.trend.trend === 'down' ? 'bg-red-900/20 border-red-700' :
                'bg-gray-800/20 border-gray-700'
              } border rounded-lg p-4 mt-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">
                      Score Trend (vs. {result.trend.daysSince} days ago)
                    </p>
                    <div className="flex items-center gap-2">
                      {result.trend.trend === 'up' && (
                        <svg className="w-6 h-6 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {result.trend.trend === 'down' && (
                        <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {result.trend.trend === 'same' && (
                        <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      )}
                      <span className={`text-2xl font-bold ${
                        result.trend.trend === 'up' ? 'text-green-400' :
                        result.trend.trend === 'down' ? 'text-red-400' :
                        'text-gray-400'
                      }`}>
                        {result.trend.trend === 'same' ? '0' : `${result.trend.trend === 'up' ? '+' : '-'}${result.trend.delta || '0'}`}
                      </span>
                      <span className="text-sm text-gray-400">points</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400">Previous Score</p>
                    <p className="text-lg font-semibold text-gray-200">{result.trend.previousScore}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(result.trend.previousDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* FIXED: Discrete grade bands with clear indicator */}
            <div className="space-y-3">
              <div className="grid grid-cols-5 gap-2">
                {[
                  { grade: 'F', min: 0, max: 59, color: 'bg-red-500' },
                  { grade: 'D', min: 60, max: 69, color: 'bg-orange-500' },
                  { grade: 'C', min: 70, max: 79, color: 'bg-yellow-500' },
                  { grade: 'B', min: 80, max: 89, color: 'bg-blue-500' },
                  { grade: 'A', min: 90, max: 100, color: 'bg-green-500' },
                ].map((band) => {
                  const isActive = overallScore >= band.min && overallScore <= band.max;
                  return (
                    <div key={band.grade} className="relative">
                      <div
                        className={`h-4 rounded transition-all ${
                          isActive ? band.color + ' ring-2 ring-white/50' : 'bg-dark-700'
                        }`}
                      />
                      {isActive && (
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex flex-col items-center">
                          <div className="px-2 py-1 bg-dark-900 border border-dark-600 rounded text-xs font-bold text-white whitespace-nowrap">
                            {overallScore}
                          </div>
                          <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-dark-900"></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Grade labels */}
              <div className="grid grid-cols-5 gap-2 text-center text-xs text-dark-500">
                <span>F<br/>(0-59)</span>
                <span>D<br/>(60-69)</span>
                <span>C<br/>(70-79)</span>
                <span>B<br/>(80-89)</span>
                <span>A<br/>(90-100)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Analyzers Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-white">Detailed Analysis</h3>
            <span className="px-3 py-1 bg-green-500/10 border border-green-500/30 rounded-full text-sm font-bold text-green-500">
              ✓ Free Feature
            </span>
          </div>
          <div className="space-y-8"> {/* Increased spacing between groups */}
            {analyzerGroups.map(group => (
              <div key={group.id} className={`p-6 rounded-lg ${group.color} border border-dark-700`}>
                <div className="mb-4">
                  <h4 className="text-xl font-bold text-white">{group.title}</h4>
                  <p className="text-sm text-dark-400 mt-1">{group.description}</p>
                </div>
                <div className="space-y-4">
                  {group.keys.map(key => {
                    const data = analyzers[key];
                    if (!data) return null; // Don't render if analyzer data is missing

                    return (
                      <React.Fragment key={key}>
                        <ImprovedAnalyzerCard
                          analyzerKey={key}
                          analyzerData={data}
                          weight={weights[key]}
                          hasError={!!analysisError}
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Site-Level E-E-A-T - Pro Feature (Weighted 15%) */}
        {(user?.subscription?.type === 'pro' || user?.subscription?.type === 'enterprise') && user?.hasActiveSubscription && (
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              Site-Level Authority Audit
              <span className="px-2 py-0.5 bg-blue-500/10 text-blue-500 text-xs font-bold rounded border border-blue-500/30">
                Weighted 15%
              </span>
            </h3>
            {/* Analyzer content is now part of the Pillars loop above */}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex-1 py-3 bg-dark-800 hover:bg-dark-700 text-white font-semibold rounded-lg transition-colors border border-dark-700"
          >
            Analyze Another Website
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex-1 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Analysis'}
          </button>
          <button
            onClick={() => handleExportPDF('summary')}
            className="px-6 py-3 bg-dark-800 hover:bg-dark-700 text-white font-semibold rounded-lg transition-colors border border-dark-700 flex items-center gap-2"
          >
            <Download size={20} />
            Export Report
            <span className="px-2 py-0.5 bg-primary-500/10 text-primary-500 text-xs font-bold rounded border border-primary-500/30">
              Pro
            </span>
          </button>
        </div>
      </div>

      <VersionFooter 
        showFairUseModal={showFairUseModal} 
        setShowFairUseModal={setShowFairUseModal} 
      />

      {/* Subscription Plans Modal - Stripe Integration */}
      {showSubscriptionPlans && (
        <SubscriptionPlans
          currentUser={user}
          onClose={() => setShowSubscriptionPlans(false)}
        />
      )}

      {/* Pricing Modal - Keep for Professional Services */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        initialTab={pricingModalTab}
        user={user}
      />

      {/* Billing Management Modal - Stripe */}
      {showBillingManagement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Manage Subscription</h2>
                <button
                  onClick={() => setShowBillingManagement(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <BillingManagement
                currentUser={user}
                onRefresh={() => {
                  setShowBillingManagement(false);
                  setShowSubscriptionPlans(true);
                }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Guide Modal */}
      <GuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
        defaultTab={guideModalTab}
      /> 
      {/* Fair Use Policy Modal */}
      <FairUsePolicyModal
        isOpen={showFairUseModal}
        onClose={() => setShowFairUseModal(false)}
      />
    </div>
    </>
  );
};

export default Dashboard;
